/**
 * DemoAccountSelector — UAT Phase 2
 *
 * Collapsible demo-account panel gated behind VITE_UAT_DEMO_MODE === "true".
 * Displays all personas for a given platform, grouped by role, with a full
 * info panel and a one-click "Login as …" button that calls `onSelect` with
 * the persona's email and password. Completely absent from production bundles
 * when the env flag is false (tree-shaken by Vite).
 *
 * Props:
 *   platform   — platform slug matching the persona registry (e.g. "crm")
 *   onSelect   — called with (email, password) when the tester clicks login
 *   className  — optional additional class names for the outer container
 */

import * as React from 'react';
import { personasForPlatform, type UatPersona } from '@workspace/uat-personas/personas';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DemoAccountSelectorProps {
  /** Platform slug — must match the `platform` array in the persona registry */
  platform: string;
  /**
   * Called when the tester clicks "Login as …".
   * When `uatApiBase` is supplied the second argument is a **JWT token** (starts
   * with "eyJ") issued by POST /api/uat/demo-login — use it directly instead of
   * treating it as a bcrypt password.  Without `uatApiBase` the second argument
   * is the plaintext dev password from the persona registry.
   */
  onSelect: (email: string, passwordOrToken: string) => void;
  /**
   * Base URL used to call POST /api/uat/demo-login (e.g. "" for same-origin
   * via Vite proxy, or "http://localhost:3000" for a direct connection).
   * When omitted the component falls back to calling onSelect with the
   * plaintext password — UAT logins will fail for personas with placeholder
   * bcrypt hashes.
   */
  uatApiBase?: string;
  /**
   * When true, the component performs a full-page redirect to "/" after a
   * successful UAT demo-login instead of calling onSelect.  Use this for
   * cookie-authenticated platforms (e.g. CRM) where the server sets the
   * httpOnly auth cookie on the demo-login response and a fresh page load
   * is the easiest way to pick it up.
   *
   * Ignored when `uatApiBase` is not supplied.
   */
  cookieAuth?: boolean;
  /** Optional additional class names for the outer container */
  className?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Group an array of personas by their `role` field. */
function groupByRole(personas: UatPersona[]): Map<string, UatPersona[]> {
  const map = new Map<string, UatPersona[]>();
  for (const p of personas) {
    const bucket = map.get(p.role);
    if (bucket) {
      bucket.push(p);
    } else {
      map.set(p.role, [p]);
    }
  }
  return map;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DemoAccountSelector({
  platform,
  onSelect,
  uatApiBase,
  cookieAuth = false,
  className = '',
}: DemoAccountSelectorProps): React.ReactElement | null {
  // Hard gate — render nothing when UAT demo mode is off
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((import.meta as any).env?.VITE_UAT_DEMO_MODE !== 'true') return null;

  const personas = React.useMemo(() => personasForPlatform(platform), [platform]);
  const groups = React.useMemo(() => groupByRole(personas), [personas]);

  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string>(() => personas[0]?.id ?? '');
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);

  const selected = React.useMemo(
    () => personas.find((p) => p.id === selectedId) ?? personas[0] ?? null,
    [personas, selectedId]
  );

  if (personas.length === 0) return null;

  const handleLogin = async () => {
    if (!selected) return;
    setLoginError(null);

    // When uatApiBase is set, fetch a real JWT from the demo-login endpoint.
    // UAT personas have non-functional bcrypt placeholder hashes — they cannot
    // be authenticated via the normal password flow.
    if (uatApiBase !== undefined) {
      setIsLoggingIn(true);
      try {
        const resp = await fetch(`${uatApiBase}/api/uat/demo-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // ensure Set-Cookie from response is stored
          body: JSON.stringify({ personaId: selected.id }),
        });
        const data = (await resp.json()) as Record<string, unknown>;
        if (!resp.ok) {
          setLoginError(
            (data?.error as string | undefined) ?? `UAT demo-login failed (HTTP ${resp.status})`
          );
          return;
        }

        // cookieAuth=true: server set the httpOnly crm_token cookie on this
        // response — a full page reload picks it up without any React state wiring.
        if (cookieAuth) {
          window.location.replace('/');
          return;
        }

        // super-admin uses localStorage tokens (sa_token / sa_api_token / sa_user).
        // Handle this in the shared component so Login.tsx doesn't need touching.
        if (platform === 'super-admin') {
          const saUser = {
            id: selected.id,
            name: selected.displayName,
            nameAr: selected.displayName,
            email: selected.email,
            role: 'super_admin',
            permissions: ['*'],
          };
          try {
            localStorage.setItem('sa_token', data.token as string);
            localStorage.setItem('sa_api_token', data.token as string);
            localStorage.setItem('sa_user', JSON.stringify(saUser));
          } catch {
            /* storage unavailable */
          }
          window.location.replace('/');
          return;
        }

        // Pass the JWT as the second argument so the platform login handler can
        // detect it (startsWith "eyJ") and skip the normal password-auth flow.
        onSelect(selected.email, data.token as string);
      } catch {
        setLoginError('Network error — is the API server running?');
      } finally {
        setIsLoggingIn(false);
      }
      return;
    }

    // Fallback: pass plaintext dev password (only works for non-placeholder hashes).
    onSelect(selected.email, selected.password);
  };

  return (
    <div
      className={`w-full rounded-lg border-2 border-amber-400 bg-amber-50 shadow-md ${className}`}
      data-testid="uat-demo-selector"
    >
      {/* Banner / toggle header */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={isOpen}
        aria-controls="uat-demo-panel"
      >
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-amber-800">⚠️ UAT MODE</span>
          <span className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-900 uppercase tracking-wide">
            Demo Accounts
          </span>
        </div>
        <span className="text-amber-700 text-sm font-medium" aria-hidden>
          {isOpen ? '▲ Hide' : '▼ Show'}
        </span>
      </button>

      {/* Collapsible body */}
      {isOpen && (
        <div id="uat-demo-panel" className="border-t border-amber-300 px-4 pb-4 pt-3 space-y-4">
          {/* Role-grouped persona dropdown */}
          <div>
            <label
              htmlFor="uat-persona-select"
              className="block text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1"
            >
              Select demo account
            </label>
            <select
              id="uat-persona-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="block w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              data-testid="uat-persona-dropdown"
            >
              {Array.from(groups.entries()).map(([role, rolePersonas]) => (
                <optgroup key={role} label={role.replace(/_/g, ' ').toUpperCase()}>
                  {rolePersonas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName} ({p.accessLevel})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Info panel for selected persona */}
          {selected && (
            <div className="rounded-md border border-amber-200 bg-white p-3 space-y-2">
              {/* Role / department / access level badges */}
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  {selected.role}
                </span>
                {selected.department && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {selected.department}
                  </span>
                )}
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  {selected.accessLevel}
                </span>
                {selected.loyaltyTier && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                    tier: {selected.loyaltyTier}
                  </span>
                )}
              </div>

              {/* Permissions summary */}
              {selected.permissionsSummary.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Permissions</p>
                  <ul className="space-y-0.5">
                    {selected.permissionsSummary.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <span className="mt-0.5 text-green-500 shrink-0">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Example workflows */}
              {selected.exampleWorkflows.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Example workflows</p>
                  <ul className="space-y-0.5">
                    {selected.exampleWorkflows.map((wf, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <span className="mt-0.5 text-blue-400 shrink-0">→</span>
                        {wf}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Credential note */}
              <div className="rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-800 space-y-0.5">
                <p>
                  <span className="font-semibold">Email:</span>{' '}
                  <code className="font-mono">{selected.email}</code>
                </p>
                <p>
                  <span className="font-semibold">Password hint:</span>{' '}
                  <code className="font-mono">
                    {selected.password.slice(0, 4)}
                    {'*'.repeat(Math.max(0, selected.password.length - 4))}
                  </code>{' '}
                  <span className="text-amber-600">(dev-only — rotate after first login)</span>
                </p>
                {selected.phone && (
                  <p>
                    <span className="font-semibold">Phone (OTP):</span>{' '}
                    <code className="font-mono">{selected.phone}</code>
                  </p>
                )}
                {selected.oauthClientId && (
                  <p>
                    <span className="font-semibold">OAuth client_id:</span>{' '}
                    <code className="font-mono">{selected.oauthClientId}</code>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Login button */}
          {loginError && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {loginError}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              void handleLogin();
            }}
            disabled={!selected || isLoggingIn}
            className="w-full rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="uat-login-btn"
          >
            {isLoggingIn
              ? 'Authenticating…'
              : selected
                ? `Login as ${selected.displayName}`
                : 'Select a persona above'}
          </button>
        </div>
      )}
    </div>
  );
}
